import Icon from '@components/v2/ui/Icon/Icon';
import { Box } from '@mui/material';
import classNames from 'classnames';
import { memo, useRef } from 'react';
import Typography from '../../../../../../../../../../../components/Typography/Typography';
import { PackQuantitiesWithMultiplier } from '../../../../../../../../../../../services/modules/product-colors-packs/product-colors-packs.types';
import HorizontalScroll from '../../../../../components/HorizontalScroll/HorizontalScroll';
import { usePackStyles } from '../../../../styles/usePackStyles';
import usePackMenuItem from '../../hooks/usePackMenuItem';
import { getPackNameColor, getPackStatusIcon } from '../../utils/orderProductPacksMenuItem.utils';
import PacksMenuItemGridMultiplyMobile from './PacksMenuItemGridMultiplyMobile';

interface PacksMenuItemMobileProps {
  pack: PackQuantitiesWithMultiplier;
  itemWidth: number;
  isPackSelected: boolean;
  isPackWithError: boolean;
  isPackWithUnavailableStock: boolean;
  onClickRow: (quantity: number) => void;
  onClickGridMultiply: (multiplier: number) => void;
}

const PacksMenuItemMobile = ({
  pack,
  itemWidth,
  onClickRow,
  isPackSelected,
  isPackWithError,
  onClickGridMultiply,
  isPackWithUnavailableStock,
}: PacksMenuItemMobileProps) => {
  const { classes: packStyles } = usePackStyles();
  const gridMultiply = useRef<HTMLInputElement>();
  const { showStock, handleInput, stockAvailableAmount, packItemInReadMode } = usePackMenuItem({
    pack,
  });

  const handleClickGridMultiply = () => {
    const multiplier = gridMultiply.current?.valueAsNumber ?? 1;
    onClickGridMultiply(multiplier);
  };

  const handleClickRow = () => {
    const multiplier = gridMultiply.current?.valueAsNumber || 1;
    onClickRow(multiplier);
  };

  const onClickIncrement = () => {
    gridMultiply.current?.stepUp();
    handleClickGridMultiply();
  };

  const onClickDecrement = () => {
    gridMultiply.current?.stepDown();
    handleClickGridMultiply();
  };

  const { icon: statusIcon, color: statusIconColor } = getPackStatusIcon({
    isPackSelected,
    isPackWithError,
  });

  const PackName = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: 26,
          display: 'flex',
          alignItems: 'center',
          color: statusIconColor,
        }}
      >
        <Icon icon={statusIcon} size="lg" />
      </Box>
      <Typography
        color={getPackNameColor({ isPackSelected, isPackWithError })}
        className={packStyles.gridName}
      >
        {pack.name.toUpperCase()}
      </Typography>
    </Box>
  );

  return (
    <Box
      className={classNames(
        {
          [packStyles.selectedItemRow]: isPackSelected && !isPackWithError,
          [packStyles.errorItemRow]: isPackSelected && isPackWithError,
        },
        packStyles.itemsContainer,
      )}
      onClick={handleClickRow}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          paddingLeft: '12px',
          height: '36px',
        }}
      >
        {PackName}
        <Box
          className={packStyles.stickyPackMultiplier}
          onClick={(event) => event.stopPropagation()}
        >
          <PacksMenuItemGridMultiplyMobile
            ref={gridMultiply}
            showStock={showStock}
            handleInput={handleInput}
            readMode={packItemInReadMode}
            packMultiplier={pack.multiplier}
            onClickIncrement={onClickIncrement}
            onClickDecrement={onClickDecrement}
            willConsumeStock={pack.willConsumeStock}
            stockAvailableAmount={stockAvailableAmount}
            handleClickGridMultiply={handleClickGridMultiply}
            isPackWithUnavailableStock={isPackWithUnavailableStock}
          />
        </Box>
      </Box>
      <HorizontalScroll>
        <div className={packStyles.itemContainer}>
          {pack.quantitiesBySize.map((item) => (
            <div
              key={`${pack.id}-${item.size.id}`}
              className={packStyles.sizeQuantityItemContainer}
              style={{ width: itemWidth }}
            >
              <Typography color="textSecondary" align="center" noWrap>
                {item.size.name}
              </Typography>
              <Typography color="textPrimary" align="center" noWrap>
                {item.quantity}
              </Typography>
            </div>
          ))}
        </div>
      </HorizontalScroll>
    </Box>
  );
};

export default memo(PacksMenuItemMobile);
